/**
 * Horizontal Event Positioning Utilities
 * Functions for calculating event positions in horizontal calendar views
 */

import type { Dayjs } from 'dayjs';
import type { CalendarEvent } from '@/api/types/calendars.types';

/**
 * Column width in pixels (from CSS)
 */
const COLUMN_WIDTH = 120;

/**
 * Event position and dimensions
 */
export interface EventPosition {
  /** Distance from left edge in pixels */
  left: number;
  /** Width of the event in pixels */
  width: number;
  /** Event starts before the visible period */
  startsBeforeView: boolean;
  /** Event ends after the visible period */
  endsAfterView: boolean;
}

/**
 * Calculate horizontal position and width for an event
 *
 * @param event - Calendar event to position
 * @param days - Array of days being displayed
 * @param hourResolution - Time resolution in hours (1, 2, or 4)
 * @param parse - Function to parse UTC time string to Dayjs in user timezone
 * @returns Event position and dimensions, or null if event is not visible
 */
export const getHorizontalEventPosition = (
  event: CalendarEvent,
  days: Dayjs[],
  hourResolution: number,
  parse: (date: string) => Dayjs
): EventPosition | null => {
  const eventStart = parse(event.start_time);
  const eventEnd = parse(event.end_time);

  // Period bounds
  const periodStart = days[0].startOf('day');
  const periodEnd = days[days.length - 1].endOf('day');

  // Check if event is within visible period
  if (eventEnd.isBefore(periodStart) || eventStart.isAfter(periodEnd)) {
    return null; // Event not visible
  }

  // Clamp event to visible range
  const clampedStart = eventStart.isBefore(periodStart)
    ? periodStart
    : eventStart;
  const clampedEnd = eventEnd.isAfter(periodEnd) ? periodEnd : eventEnd;

  // Calculate position in minutes from period start
  const minutesFromStart = clampedStart.diff(periodStart, 'minute');
  const durationMinutes = clampedEnd.diff(clampedStart, 'minute');

  // Convert to column units (each column = hourResolution hours)
  const minutesPerColumn = hourResolution * 60;

  const left = (minutesFromStart / minutesPerColumn) * COLUMN_WIDTH;
  const width = (durationMinutes / minutesPerColumn) * COLUMN_WIDTH;

  return {
    left,
    width: Math.max(width, 30), // Minimum width for visibility
    startsBeforeView: eventStart.isBefore(periodStart),
    endsAfterView: eventEnd.isAfter(periodEnd),
  };
};

/**
 * Event overlap lane assignment
 */
export interface EventLane {
  /** Event ID */
  eventId: string;
  /** Vertical lane (0 = top, 1 = second, etc.) */
  lane: number;
}

/**
 * Detect overlapping events within same resource and assign vertical lanes
 *
 * @param events - Array of calendar events for a single resource
 * @param resourceId - ID of the resource
 * @param days - Array of days being displayed
 * @param hourResolution - Time resolution in hours
 * @param parse - Function to parse UTC time string to Dayjs
 * @returns Map of event ID to lane number
 */
export const calculateEventOverlaps = (
  events: CalendarEvent[],
  resourceId: string,
  days: Dayjs[],
  hourResolution: number,
  parse: (date: string) => Dayjs
): Map<string, number> => {
  const lanes = new Map<string, number>();

  // Filter events for this resource and calculate positions
  const resourceEvents = events
    .filter((event) =>
      event.resources.some((r) => r.resource_id === resourceId)
    )
    .map((event) => ({
      event,
      position: getHorizontalEventPosition(event, days, hourResolution, parse),
      start: parse(event.start_time),
      end: parse(event.end_time),
    }))
    .filter((item) => item.position !== null)
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());

  // Track which lanes are occupied at each time point
  const occupiedLanes: Array<{ end: Dayjs; lane: number }> = [];

  resourceEvents.forEach(({ event, start, end }) => {
    // Remove lanes that have ended before this event starts
    const availableLanes = occupiedLanes.filter((occupied) =>
      occupied.end.isAfter(start)
    );

    // Find the first available lane
    let assignedLane = 0;
    const usedLanes = new Set(availableLanes.map((l) => l.lane));

    while (usedLanes.has(assignedLane)) {
      assignedLane++;
    }

    // Assign lane to event
    lanes.set(event.id, assignedLane);

    // Mark this lane as occupied until event ends
    occupiedLanes.push({ end, lane: assignedLane });
  });

  return lanes;
};

/**
 * Calculate row height needed to accommodate all overlapping events
 *
 * @param events - Array of calendar events for a single resource
 * @param resourceId - ID of the resource
 * @param days - Array of days being displayed
 * @param hourResolution - Time resolution in hours
 * @param parse - Function to parse UTC time string to Dayjs
 * @returns Minimum row height in pixels
 */
export const calculateRowHeight = (
  events: CalendarEvent[],
  resourceId: string,
  days: Dayjs[],
  hourResolution: number,
  parse: (date: string) => Dayjs
): number => {
  const lanes = calculateEventOverlaps(
    events,
    resourceId,
    days,
    hourResolution,
    parse
  );

  if (lanes.size === 0) {
    return 80; // Default row height with no events
  }

  const maxLane = Math.max(...Array.from(lanes.values()));
  const eventHeight = 60; // Height of each event
  const eventGap = 8; // Gap between events

  return (maxLane + 1) * (eventHeight + eventGap) + 16; // +16 for padding
};
